import { useEffect, useId, useRef, type MouseEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

export interface ErrorSummaryItem {
  /** Id of the field to focus (first input of a date input / first option of radios and checkboxes). */
  targetId?: string;
  /** Link to another page instead of a field on this page (e.g. a wizard step). */
  to?: string;
  text: string;
}

export interface ErrorSummaryProps {
  errors: ErrorSummaryItem[];
  title?: string;
  description?: string;
  /** Change this value to move focus to the summary again (e.g. a submit counter). */
  focusKey?: unknown;
  children?: React.ReactNode;
}

/** Scrolls the field's label/legend into view and focuses the field (GOV.UK error-summary behaviour). */
export function focusField(targetId: string): boolean {
  const input = document.getElementById(targetId);
  if (!input) return false;
  const legend = input.closest('fieldset')?.querySelector('legend');
  const label = document.querySelector(`label[for="${CSS.escape(targetId)}"]`);
  const anchor = legend ?? label ?? input;
  anchor.scrollIntoView?.();
  input.focus({ preventScroll: true });
  return true;
}

/** govuk/components/error-summary – focused when shown; links move focus to the field. */
export function ErrorSummary({ errors, title, description, focusKey, children }: ErrorSummaryProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = `error-summary-title-${useId().replace(/:/g, '')}`;
  const hasErrors = errors.length > 0 || Boolean(children);
  useEffect(() => {
    if (hasErrors) ref.current?.focus();
  }, [hasErrors, focusKey]);
  if (!hasErrors) return null;
  const onClick = (event: MouseEvent<HTMLAnchorElement>, targetId: string) => {
    if (focusField(targetId)) event.preventDefault();
  };
  return (
    <div className="govuk-error-summary" aria-labelledby={titleId} role="alert" tabIndex={-1} ref={ref} data-module="govuk-error-summary">
      <h2 className="govuk-error-summary__title" id={titleId}>
        {title ?? t('errorSummary.title')}
      </h2>
      <div className="govuk-error-summary__body">
        {description && <p>{description}</p>}
        {children}
        {errors.length > 0 && (
          <ul className="govuk-list govuk-error-summary__list">
            {errors.map((error, i) => (
              <li key={`${error.targetId ?? ''}-${i}`}>
                {error.to ? (
                  <Link to={error.to}>{error.text}</Link>
                ) : error.targetId ? (
                  <a href={`#${error.targetId}`} onClick={(e) => onClick(e, error.targetId!)}>
                    {error.text}
                  </a>
                ) : (
                  error.text
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
